import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../../api/api';
import AuthContext from '../../contexts/AuthContext';
import QuranService from '../../services/quranService';
import { createAssignment } from '../../services/assignmentService';
import * as DatabaseUtil from '../../utils/databaseUtil';
import VersePreview from '../../components/VersePreview';
import '../../styles/CreateAssignmentPage.css';

const CreateAssignmentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  // Get any passed params from location state
  const initialStudentId = location.state?.studentId || null;
  const initialStudentName = location.state?.studentName || null;
  const initialHalaqaId = location.state?.halaqaId || null;
  const initialHalaqaName = location.state?.halaqaName || null;
  
  // Check if we're in edit mode
  const isEditMode = location.state?.isEditMode || false;
  const assignmentId = location.state?.assignmentId || null;
  
  console.log('CreateAssignmentPage - Mode:', isEditMode ? 'Edit' : 'Create');
  console.log('Assignment ID:', assignmentId);
  
  // State for general UI
  const [loading, setLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  
  // State for verse preview
  const [showVersePreview, setShowVersePreview] = useState(false);
  const [loadingVerses, setLoadingVerses] = useState(false);
  const [verses, setVerses] = useState([]);
  const [teachingTips, setTeachingTips] = useState([]);
  
  // State for dropdowns
  const [showStartSurahDropdown, setShowStartSurahDropdown] = useState(false);
  const [showEndSurahDropdown, setShowEndSurahDropdown] = useState(false);
  const [showStudentsDropdown, setShowStudentsDropdown] = useState(false);
  const [startSurahSearchText, setStartSurahSearchText] = useState('');
  const [endSurahSearchText, setEndSurahSearchText] = useState('');
  
  // Legacy compatibility
  const showSurahDropdown = showStartSurahDropdown;
  const searchText = startSurahSearchText;
  
  // Assignment data
  const [assignmentType, setAssignmentType] = useState('new-memorization');
  
  // Multi-surah support
  const [isMultiSurah, setIsMultiSurah] = useState(false);
  const [startSurah, setStartSurah] = useState(null);
  const [endSurah, setEndSurah] = useState(null);
  const [startVerse, setStartVerse] = useState('1');
  const [endVerse, setEndVerse] = useState('');
  
  // Legacy single-surah support (for backward compatibility)
  const selectedSurah = isMultiSurah ? null : startSurah;
  const fromVerse = startVerse;
  const toVerse = isMultiSurah ? endVerse : startVerse === endVerse ? endVerse : endVerse;
  
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Set due date to tomorrow by default
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  });
  const [matchingQuiz, setMatchingQuiz] = useState(false);
  
  // State to store original halaqaId from fetched assignment (for edit mode)
  const [originalHalaqaId, setOriginalHalaqaId] = useState(null);

  // Surah options
  const [surahOptions, setSurahOptions] = useState([]);
  const [loadingAssignment, setLoadingAssignment] = useState(isEditMode);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Phase 1.6: Recalculation support
  const [recalculateAfter, setRecalculateAfter] = useState(false);
  const [showRecalculationConfirm, setShowRecalculationConfirm] = useState(false);
  const [subsequentPendingCount, setSubsequentPendingCount] = useState(0);

  // Load Surahs and Halaqa data on init
  useEffect(() => {
    // Initialize database for teaching tips
    const initDb = async () => {
      await DatabaseUtil.initDatabase();
    };
    
    initDb();
    
    // Load surahs
    const surahs = QuranService.getAllSurahs();
    setSurahOptions(surahs);
    
    // Fetch classes/halaqas
    const fetchClasses = async () => {
      try {
        const response = await api.get('/halaqas');
        const classesData = response.data.success 
          ? response.data.data 
          : response.data;
        const classesArray = Array.isArray(classesData) ? classesData : [];
        setClasses(classesArray);
        
        // If we have an initial halaqaId from params, select it
        if (initialHalaqaId) {
          // Find the halaqa in the array to get its details
          const selectedHalaqa = classesArray.find(c => c._id === initialHalaqaId);
          if (selectedHalaqa) {
            // Fetch students for this halaqa
            fetchStudentsForHalaqa(initialHalaqaId);
          }
        }
      } catch (error) {
        console.error('Error fetching classes:', error);
      }
    };
    
    fetchClasses();
    
    // If we have initialStudentId, initialize selectedStudents
    if (initialStudentId && initialStudentName) {
      setSelectedStudents([{
        id: initialStudentId,
        name: initialStudentName
      }]);
    }
  }, [initialHalaqaId, initialStudentId, initialStudentName]);
  
  // Store assignment data for student processing in a separate step
  const [fetchedAssignmentData, setFetchedAssignmentData] = useState(null);
  
  // Step 1: Fetch assignment data
  useEffect(() => {
    // Only fetch assignment data when in edit mode with a valid assignmentId
    if (isEditMode && assignmentId) {
      const fetchAssignmentData = async () => {
        try {
          setLoadingAssignment(true);
          
          // Import the necessary service method
          const { getAssignmentById } = await import('../../services/assignmentService');
          
          const assignmentData = await getAssignmentById(assignmentId);
          console.log('Assignment data received:', assignmentData);
          
          // Populate form with assignment data
          setAssignmentType(assignmentData.type || 'new-memorization');
          
          // Handle both legacy (surah/verses) and new (range) format
          if (assignmentData.range) {
            // New format: extract start and end surahs from range
            const startSurahData = surahOptions.find(s => s.id === assignmentData.range.start.surah.id) || assignmentData.range.start.surah;
            const endSurahData = surahOptions.find(s => s.id === assignmentData.range.end.surah.id) || assignmentData.range.end.surah;
            
            setStartSurah(startSurahData);
            setEndSurah(endSurahData);
            setStartVerse(assignmentData.range.start.verse.toString());
            setEndVerse(assignmentData.range.end.verse.toString());
            
            // Determine if this is a multi-surah assignment
            setIsMultiSurah(startSurahData.id !== endSurahData.id);
          } else if (assignmentData.surah && assignmentData.verses) {
            // Legacy format: convert to new state
            const surah = surahOptions.find(s => s.id === assignmentData.surah.id) || assignmentData.surah;
            setStartSurah(surah);
            setEndSurah(surah); // Same surah for both start and end
            setStartVerse(assignmentData.verses.from.toString());
            setEndVerse(assignmentData.verses.to.toString());
            setIsMultiSurah(false); // Legacy format is always single-surah
          }
          
          // Set due date
          if (assignmentData.dueDate) {
            const date = new Date(assignmentData.dueDate);
            setSelectedDate(date.toISOString().split('T')[0]);
          }
          
          // Set matching quiz flag if available
          if (assignmentData.matchingQuiz !== undefined) {
            setMatchingQuiz(assignmentData.matchingQuiz);
          }
          
          // Store original halaqaId from fetched assignment for edit mode
          if (assignmentData.halaqaId) {
            setOriginalHalaqaId(assignmentData.halaqaId);
            console.log('Preserving original halaqaId for edit mode:', assignmentData.halaqaId);
          }
          
          // Store data for student processing
          setFetchedAssignmentData(assignmentData);

          // Only fetch students for this halaqa if needed
          if (assignmentData.halaqaId) {
            await fetchStudentsForHalaqa(assignmentData.halaqaId);
          }

          // Phase 1.6: Fetch subsequent pending assignments count for recalculation feature
          if (assignmentData.planId) {
            try {
              const response = await api.get(`/learning-plans/${assignmentData.planId}/assignments`);
              const allAssignments = response.data.data || response.data || [];

              // Count pending assignments after this one
              const subsequentPending = allAssignments.filter(a =>
                a._id !== assignmentId &&
                new Date(a.dueDate) > new Date(assignmentData.dueDate) &&
                a.status !== 'completed'
              );

              setSubsequentPendingCount(subsequentPending.length);
            } catch (error) {
              console.error('Error fetching subsequent assignments:', error);
              // Non-critical error, don't block the edit
            }
          }

        } catch (error) {
          console.error('Error fetching assignment:', error);
          window.alert('Failed to load assignment data');
        } finally {
          setLoadingAssignment(false);
        }
      };

      fetchAssignmentData();
    }
  }, [isEditMode, assignmentId, surahOptions]);
  
  // Step 2: Process student selection separately to avoid re-rendering loop
  useEffect(() => {
    if (fetchedAssignmentData && fetchedAssignmentData.students && fetchedAssignmentData.students.length > 0 && students.length > 0) {
      try {
        if (typeof fetchedAssignmentData.students[0] === 'object' && fetchedAssignmentData.students[0]._id) {
          // If students are objects with _id
          const selectedIds = fetchedAssignmentData.students.map(s => s._id);
          
          // Find the selected students in the students array
          const selectedStudentObjects = students.filter(student => 
            selectedIds.includes(student.id)
          );
          setSelectedStudents(selectedStudentObjects);
        } else {
          // If students are just IDs
          const selectedStudentObjects = students.filter(student => 
            fetchedAssignmentData.students.includes(student.id)
          );
          setSelectedStudents(selectedStudentObjects);
        }
      } catch (error) {
        console.error('Error processing student selection:', error);
      }
    }
  }, [fetchedAssignmentData, students]);
  
  // Function to fetch students for a halaqa
  const fetchStudentsForHalaqa = async (halaqaId) => {
    if (!halaqaId) {
      setStudents([]);
      return;
    }
    
    try {
      const response = await api.get(`/halaqas/${halaqaId}/students`);
      const studentsData = response.data.success 
        ? response.data.data 
        : response.data;
      const formattedStudents = (Array.isArray(studentsData) ? studentsData : [])
        .map(student => ({
          id: student._id,
          name: student.name
        }));
      setStudents(formattedStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
    }
  };

  // Handler for assignment type change
  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type);
    // Clear errors
    if (formErrors.assignmentType) {
      setFormErrors(prev => ({ ...prev, assignmentType: '' }));
    }
  };
  
  // Handler for multi-surah toggle
  const handleMultiSurahToggle = () => {
    setIsMultiSurah(prev => {
      const newValue = !prev;
      
      if (!newValue) {
        // Switching to single-surah: use start surah for both
        setEndSurah(startSurah);
        if (startSurah) {
          const verseCount = QuranService.getVerseCount(startSurah.id);
          setEndVerse(verseCount.toString());
        }
      }
      
      return newValue;
    });
  };
  
  // Handler for start surah selection
  const handleSelectStartSurah = (surah) => {
    setStartSurah(surah);
    
    // Auto-set end surah to same surah if not multi-surah mode
    if (!isMultiSurah) {
      setEndSurah(surah);
    }
    
    setShowStartSurahDropdown(false);
    setStartSurahSearchText('');
    
    // Auto-populate verses
    const verseCount = QuranService.getVerseCount(surah.id);
    if (!isMultiSurah) {
      setEndVerse(verseCount.toString());
    }
    
    // Clear errors
    if (formErrors.surah || formErrors.startSurah) {
      setFormErrors(prev => ({ ...prev, surah: '', startSurah: '' }));
    }
  };
  
  // Handler for end surah selection (multi-surah only)
  const handleSelectEndSurah = (surah) => {
    setEndSurah(surah);
    setShowEndSurahDropdown(false);
    setEndSurahSearchText('');
    
    // Auto-populate end verse with last verse of end surah
    const verseCount = QuranService.getVerseCount(surah.id);
    setEndVerse(verseCount.toString());
    
    // Clear errors
    if (formErrors.endSurah) {
      setFormErrors(prev => ({ ...prev, endSurah: '' }));
    }
  };
  
  // Legacy handler for backward compatibility
  const handleSelectSurah = handleSelectStartSurah;
  
  // Handler for verse input changes
  const handleVerseInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'startVerse' || name === 'fromVerse') {
      setStartVerse(value);
    } else if (name === 'endVerse' || name === 'toVerse') {
      setEndVerse(value);
    }
    
    // Clear errors
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  // Handler for halaqa (class) selection
  const handleHalaqaChange = (e) => {
    const halaqaId = e.target.value;
    
    // Fetch students for this halaqa
    fetchStudentsForHalaqa(halaqaId);
    
    // Clear selected students when changing halaqa
    setSelectedStudents([]);
    
    // Clear errors
    if (formErrors.halaqaId) {
      setFormErrors(prev => ({ ...prev, halaqaId: '' }));
    }
  };
  
  // Handler for date selection
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    
    // Clear errors
    if (formErrors.dueDate) {
      setFormErrors(prev => ({ ...prev, dueDate: '' }));
    }
  };
  
  // Handler for matching quiz toggle
  const handleMatchingQuizToggle = () => {
    setMatchingQuiz(prev => !prev);
  };
  
  // Handler for student selection
  const handleToggleStudentSelection = (student) => {
    setSelectedStudents(prev => {
      // Check if student is already selected
      const isSelected = prev.some(s => s.id === student.id);
      
      if (isSelected) {
        // Remove student
        return prev.filter(s => s.id !== student.id);
      } else {
        // Add student
        return [...prev, student];
      }
    });
    
    // Clear errors
    if (formErrors.students) {
      setFormErrors(prev => ({ ...prev, students: '' }));
    }
  };
  
  // Handler for student dropdown toggle
  const toggleStudentDropdown = () => {
    setShowStudentsDropdown(prev => !prev);
    // Close surah dropdowns if open
    if (showStartSurahDropdown) {
      setShowStartSurahDropdown(false);
      setStartSurahSearchText('');
    }
    if (showEndSurahDropdown) {
      setShowEndSurahDropdown(false);
      setEndSurahSearchText('');
    }
  };
  
  // Handlers for surah search
  const handleStartSurahSearch = (e) => {
    setStartSurahSearchText(e.target.value);
  };
  
  const handleEndSurahSearch = (e) => {
    setEndSurahSearchText(e.target.value);
  };
  
  // Legacy handler for backward compatibility
  const handleSurahSearch = handleStartSurahSearch;
  
  // Handler for clicking outside dropdowns to close them
  const handleOutsideClick = () => {
    if (showStartSurahDropdown) {
      setShowStartSurahDropdown(false);
      setStartSurahSearchText('');
    }
    if (showEndSurahDropdown) {
      setShowEndSurahDropdown(false);
      setEndSurahSearchText('');
    }
    if (showStudentsDropdown) {
      setShowStudentsDropdown(false);
    }
  };

  // Handler for verse preview
  const handlePreviewVerses = async () => {
    if (!startSurah) {
      setFormErrors(prev => ({ ...prev, startSurah: 'Please select a start surah first' }));
      return;
    }
    
    if (isMultiSurah && !endSurah) {
      setFormErrors(prev => ({ ...prev, endSurah: 'Please select an end surah for multi-surah assignment' }));
      return;
    }
    
    if (!startVerse || !endVerse) {
      setFormErrors(prev => ({ 
        ...prev, 
        startVerse: !startVerse ? 'Required' : '',
        endVerse: !endVerse ? 'Required' : ''
      }));
      return;
    }
    
    // Basic validation
    if (!isMultiSurah && parseInt(startVerse) > parseInt(endVerse)) {
      setFormErrors(prev => ({ 
        ...prev, 
        startVerse: 'Start verse must be less than or equal to end verse'
      }));
      return;
    }
    
    setLoadingVerses(true);
    setShowVersePreview(true);
    
    try {
      let fetchedVerses = [];
      let allTips = {};
      
      if (!isMultiSurah || startSurah.id === (endSurah || startSurah).id) {
        // Single surah: use existing logic
        fetchedVerses = await QuranService.fetchVerses(
          startSurah.id, 
          parseInt(startVerse), 
          parseInt(endVerse)
        );
        
        // Fetch teaching tips for single surah
        try {
          console.log(`Fetching teaching tips for Surah ${startSurah.id}, verses ${startVerse}-${endVerse}...`);
          const tips = await DatabaseUtil.fetchTeachingTips(
            parseInt(startSurah.id),
            parseInt(startVerse),
            parseInt(endVerse)
          );
          allTips = tips;
        } catch (error) {
          console.error("Error fetching teaching tips:", error);
        }
      } else {
        // Multi-surah: fetch verses from multiple surahs
        console.log(`Fetching multi-surah verses from ${startSurah.name}:${startVerse} to ${endSurah.name}:${endVerse}`);
        
        // For now, show a placeholder for multi-surah preview
        // This can be enhanced later to fetch actual verses from multiple surahs
        fetchedVerses = [
          {
            number: 1,
            numberInSurah: parseInt(startVerse),
            surah: {
              number: parseInt(startSurah.id),
              name: startSurah.name,
              englishName: startSurah.name
            },
            text: `Multi-surah assignment: ${startSurah.name} (${startVerse}) to ${endSurah.name} (${endVerse})`,
            translation: 'Preview not available for multi-surah assignments yet.'
          }
        ];
      }
      
      setVerses(fetchedVerses || []);
      setTeachingTips(allTips);
      
    } catch (error) {
      console.error('Error fetching verses:', error);
      // Use placeholder data if API fails
      setVerses([
        {
          number: 1,
          numberInSurah: parseInt(startVerse),
          surah: {
            number: parseInt(startSurah.id),
            name: startSurah.name,
            englishName: startSurah.name
          },
          text: `Error loading preview for ${startSurah.name}`,
          translation: 'Preview not available due to error.'
        }
      ]);
    } finally {
      setLoadingVerses(false);
    }
  };
  
  const validateForm = () => {
    const errors = {};
    
    // Required fields
    if (!startSurah) errors.startSurah = 'Start surah selection is required';
    if (isMultiSurah && !endSurah) errors.endSurah = 'End surah selection is required';
    if (!startVerse.trim()) errors.startVerse = 'Start verse is required';
    if (!endVerse.trim()) errors.endVerse = 'End verse is required';
    if (selectedStudents.length === 0) errors.students = 'Please select at least one student';
    if (!selectedDate) errors.dueDate = 'Due date is required';
    
    // Multi-surah validation
    if (isMultiSurah && startSurah && endSurah) {
      const startSurahId = parseInt(startSurah.id);
      const endSurahId = parseInt(endSurah.id);

      // Allow both forward and backward cross-surah ranges (for learning plan compatibility)
      // No validation needed for surah order - both directions are valid

      // Only validate verse order when it's the same surah (forward only within same surah)
      if (startSurahId === endSurahId && parseInt(startVerse) > parseInt(endVerse)) {
        errors.endVerse = 'End verse must come after start verse in the same surah';
      }
    }
    
    // Single-surah validation (when not multi-surah)
    if (!isMultiSurah && startVerse && endVerse && parseInt(startVerse) > parseInt(endVerse)) {
      errors.startVerse = 'Start verse must be less than or equal to end verse';
    }
    
    // Verse range validation
    if (startSurah && startVerse && parseInt(startVerse) < 1) {
      errors.startVerse = 'Start verse must be greater than 0';
    }
    
    if (startSurah && startVerse) {
      const maxStartVerse = QuranService.getVerseCount(startSurah.id);
      if (parseInt(startVerse) > maxStartVerse) {
        errors.startVerse = `Start verse cannot exceed ${maxStartVerse} for ${startSurah.name}`;
      }
    }
    
    if (endSurah && endVerse) {
      const maxEndVerse = QuranService.getVerseCount(endSurah.id);
      if (parseInt(endVerse) > maxEndVerse) {
        errors.endVerse = `End verse cannot exceed ${maxEndVerse} for ${endSurah.name}`;
      }
    }
    
    // Legacy compatibility errors
    if (errors.startSurah) errors.surah = errors.startSurah;
    if (errors.startVerse) errors.fromVerse = errors.startVerse;
    if (errors.endVerse) errors.toVerse = errors.endVerse;
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Phase 1.6: If recalculation is checked and there are subsequent pending assignments, show confirmation
    if (isEditMode && recalculateAfter && subsequentPendingCount > 0) {
      setShowRecalculationConfirm(true);
      return;
    }

    // Proceed with normal submit
    await performSubmit();
  };

  const performSubmit = async () => {
    setLoading(true);

    try {
      // Format the assignment data using new range structure
      const assignmentData = {
        type: assignmentType,
        range: {
          start: {
            surah: {
              id: startSurah.id,
              name: startSurah.name
            },
            verse: parseInt(startVerse)
          },
          end: {
            surah: {
              id: (endSurah || startSurah).id,
              name: (endSurah || startSurah).name
            },
            verse: parseInt(endVerse)
          }
        },
        students: selectedStudents.map(student => student.id),
        center: user?.center || null,
        dueDate: selectedDate,
        matchingQuiz: matchingQuiz,
        halaqaId: originalHalaqaId || initialHalaqaId // Use original halaqaId in edit mode, fallback to initial context
      };

      // Auto-generate title and description based on range
      if (isMultiSurah && startSurah.id !== (endSurah || startSurah).id) {
        assignmentData.title = `${startSurah.name} (${startVerse}) to ${(endSurah || startSurah).name} (${endVerse})`;
        assignmentData.description = `${assignmentType} assignment: ${startSurah.name} to ${(endSurah || startSurah).name}`;
      } else {
        assignmentData.title = `${startSurah.name} (${startVerse}-${endVerse})`;
        assignmentData.description = `${assignmentType} assignment for ${startSurah.name}`;
      }

      console.log('=== FRONTEND ASSIGNMENT DATA ===');
      console.log('Assignment data before sending:', JSON.stringify(assignmentData, null, 2));

      // Determine if we're creating or updating
      if (isEditMode && assignmentId) {
        // Phase 1.6: Use edit-and-recalculate endpoint if recalculation is checked
        if (recalculateAfter && fetchedAssignmentData?.planId) {
          const response = await api.put(`/assignments/${assignmentId}/edit-and-recalculate`, {
            updates: assignmentData,
            recalculate: true
          });

          const summary = response.data.data?.regenerationSummary;
          if (summary && summary.newAssignmentsCreated > 0) {
            window.alert(
              `Assignment updated and ${summary.newAssignmentsCreated} assignment(s) recalculated!\n\n` +
              `${summary.pendingAssignmentsDeleted} pending assignment(s) were replaced.`
            );
          } else {
            window.alert('Assignment updated successfully!');
          }
        } else {
          // Standard update
          const { updateAssignment } = await import('../../services/assignmentService');
          await updateAssignment(assignmentId, assignmentData);
          window.alert('Assignment updated successfully!');
        }
      } else {
        await createAssignment(assignmentData);
        window.alert('Assignment created successfully!');
      }

      navigate(-1);
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} assignment:`, error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} assignment`
      }));
    } finally {
      setLoading(false);
    }
  };
  
  // Add delete assignment functionality
  const handleDeleteAssignment = async () => {
    if (!isEditMode || !assignmentId) return;
    
    if (!window.confirm('Are you sure you want to delete this assignment? This action cannot be undone.')) {
      return;
    }
    
    setDeleteLoading(true);
    
    try {
      // Import the deleteAssignment function
      const { deleteAssignment } = await import('../../services/assignmentService');
      await deleteAssignment(assignmentId);
      window.alert('Assignment deleted successfully!');
      navigate(-1);
    } catch (error) {
      console.error('Error deleting assignment:', error);
      setFormErrors(prev => ({
        ...prev,
        submit: error.response?.data?.message || 'Failed to delete assignment'
      }));
    } finally {
      setDeleteLoading(false);
    }
  };

  // Show loading indicator when fetching assignment for editing
  if (loadingAssignment) {
    return (
      <div className="create-assignment-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading assignment data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="create-assignment-container">
      <div className="header">
        <h1>{isEditMode ? 'Edit Assignment' : 'Create New Assignment'}</h1>
        <button 
          onClick={() => navigate(-1)} 
          className="back-btn"
        >
          ← Back
        </button>
      </div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {formErrors.submit && (
            <div className="error-message">{formErrors.submit}</div>
          )}
          
          {/* Assignment Type Section */}
          <div className="form-section">
            <h2>Assignment Type</h2>
            <div className="assignment-type-buttons">
              <button
                type="button"
                className={`type-button ${assignmentType === 'new-memorization' ? 'active' : ''}`}
                onClick={() => handleAssignmentTypeChange('new-memorization')}
              >
                <span className="type-icon">📚</span>
                <span>New Memorization</span>
              </button>
              <button
                type="button"
                className={`type-button ${assignmentType === 'revision' ? 'active' : ''}`}
                onClick={() => handleAssignmentTypeChange('revision')}
              >
                <span className="type-icon">🔄</span>
                <span>Revision</span>
              </button>
              <button
                type="button"
                className={`type-button ${assignmentType === 'recitation' ? 'active' : ''}`}
                onClick={() => handleAssignmentTypeChange('recitation')}
              >
                <span className="type-icon">🎤</span>
                <span>Recitation</span>
              </button>
            </div>
          </div>
          
          {/* Quran Content Section */}
          <div className="form-section">
            <h2>Quran Content</h2>
            
            {/* Multi-Surah Toggle */}
            <div className="form-group switch-container">
              <label htmlFor="multiSurah">Multi-Surah Assignment</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="multiSurah"
                  checked={isMultiSurah}
                  onChange={handleMultiSurahToggle}
                />
                <label htmlFor="multiSurah" className="switch-label"></label>
              </div>
              <small className="helper-text">
                {isMultiSurah ? 'Assignment covers a sequential range across surahs' : 'Assignment within single surah'}
              </small>
            </div>
            
            {/* Start Surah Selection */}
            <div className="form-group">
              <label>{isMultiSurah ? 'Start Surah' : 'Surah'}</label>
              <div 
                className={`dropdown-field ${formErrors.startSurah || formErrors.surah ? 'error' : ''}`}
                onClick={() => {
                  setShowStartSurahDropdown(!showStartSurahDropdown);
                  setShowEndSurahDropdown(false);
                  setShowStudentsDropdown(false);
                }}
              >
                <span className={startSurah ? 'selected-text' : 'placeholder-text'}>
                  {startSurah 
                    ? `${startSurah.name} ${startSurah.nameArabic ? `(${startSurah.nameArabic})` : ''}` 
                    : `Select ${isMultiSurah ? 'Start ' : ''}Surah`}
                </span>
                <span className="dropdown-icon">▼</span>
              </div>
              {(formErrors.startSurah || formErrors.surah) && <div className="error-text">{formErrors.startSurah || formErrors.surah}</div>}
            </div>
            
            {/* End Surah Selection (Multi-Surah Only) */}
            {isMultiSurah && (
              <div className="form-group">
                <label>End Surah</label>
                <div 
                  className={`dropdown-field ${formErrors.endSurah ? 'error' : ''}`}
                  onClick={() => {
                    setShowEndSurahDropdown(!showEndSurahDropdown);
                    setShowStartSurahDropdown(false);
                    setShowStudentsDropdown(false);
                  }}
                >
                  <span className={endSurah ? 'selected-text' : 'placeholder-text'}>
                    {endSurah 
                      ? `${endSurah.name} ${endSurah.nameArabic ? `(${endSurah.nameArabic})` : ''}` 
                      : 'Select End Surah'}
                  </span>
                  <span className="dropdown-icon">▼</span>
                </div>
                {formErrors.endSurah && <div className="error-text">{formErrors.endSurah}</div>}
              </div>
            )}
            
            {/* Start Surah Dropdown */}
            {showStartSurahDropdown && (
              <>
                <div className="dropdown-overlay" onClick={handleOutsideClick}></div>
                <div className="dropdown-container">
                  <div className="search-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search surahs..."
                      value={startSurahSearchText}
                      onChange={handleStartSurahSearch}
                      autoFocus
                    />
                  </div>
                  <div className="dropdown-list">
                    {surahOptions
                      .filter(surah =>
                        surah.name.toLowerCase().includes(startSurahSearchText.toLowerCase()) ||
                        (surah.nameArabic && surah.nameArabic.includes(startSurahSearchText))
                      )
                      .map((surah) => (
                        <div
                          key={`start-surah-${surah.id}`}
                          className="dropdown-item"
                          onClick={() => handleSelectStartSurah(surah)}
                        >
                          {surah.name} {surah.nameArabic ? `(${surah.nameArabic})` : ''}
                        </div>
                      ))
                    }
                  </div>
                </div>
              </>
            )}
            
            {/* End Surah Dropdown (Multi-Surah Only) */}
            {isMultiSurah && showEndSurahDropdown && (
              <>
                <div className="dropdown-overlay" onClick={handleOutsideClick}></div>
                <div className="dropdown-container">
                  <div className="search-container">
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search surahs..."
                      value={endSurahSearchText}
                      onChange={handleEndSurahSearch}
                      autoFocus
                    />
                  </div>
                  <div className="dropdown-list">
                    {surahOptions
                      .filter(surah =>
                        surah.name.toLowerCase().includes(endSurahSearchText.toLowerCase()) ||
                        (surah.nameArabic && surah.nameArabic.includes(endSurahSearchText))
                      )
                      .map((surah) => (
                        <div
                          key={`end-surah-${surah.id}`}
                          className="dropdown-item"
                          onClick={() => handleSelectEndSurah(surah)}
                        >
                          {surah.name} {surah.nameArabic ? `(${surah.nameArabic})` : ''}
                        </div>
                      ))
                    }
                  </div>
                </div>
              </>
            )}
            
            {/* Verse Range */}
            <div className="verse-range">
              <div className="form-group half-width">
                <label htmlFor="startVerse">{isMultiSurah ? 'Start Verse' : 'From Verse'}</label>
                <input
                  type="number"
                  id="startVerse"
                  name="startVerse"
                  value={startVerse}
                  onChange={handleVerseInputChange}
                  className={formErrors.startVerse || formErrors.fromVerse ? 'error' : ''}
                  min="1"
                  max={startSurah ? QuranService.getVerseCount(startSurah.id) : '999'}
                />
                {(formErrors.startVerse || formErrors.fromVerse) && (
                  <div className="error-text">{formErrors.startVerse || formErrors.fromVerse}</div>
                )}
              </div>
              
              <div className="form-group half-width">
                <label htmlFor="endVerse">{isMultiSurah ? 'End Verse' : 'To Verse'}</label>
                <input
                  type="number"
                  id="endVerse"
                  name="endVerse"
                  value={endVerse}
                  onChange={handleVerseInputChange}
                  className={formErrors.endVerse || formErrors.toVerse ? 'error' : ''}
                  min="1"
                  max={(endSurah || startSurah) ? QuranService.getVerseCount((endSurah || startSurah).id) : '999'}
                />
                {(formErrors.endVerse || formErrors.toVerse) && (
                  <div className="error-text">{formErrors.endVerse || formErrors.toVerse}</div>
                )}
              </div>
            </div>
            
            {/* Preview Button */}
            <button
              type="button"
              className="preview-button"
              onClick={handlePreviewVerses}
              disabled={!startSurah || !startVerse || !endVerse || (isMultiSurah && !endSurah)}
            >
              Preview Verses & Teaching Tips
            </button>
            
            {/* Range Summary */}
            {startSurah && startVerse && endVerse && (!isMultiSurah || endSurah) && (
              <div className="range-summary">
                <small className="helper-text">
                  {isMultiSurah && startSurah.id !== (endSurah || startSurah).id
                    ? `Range: ${startSurah.name} (${startVerse}) to ${(endSurah || startSurah).name} (${endVerse})`
                    : `Range: ${startSurah.name} (${startVerse}-${endVerse})`
                  }
                </small>
              </div>
            )}
          </div>
          
          {/* Class and Students Section */}
          <div className="form-section">
            <h2>Assign To</h2>
            
            {/* Student Selection */}
            <div className="form-group">
              <label>Students</label>
              <div 
                className={`dropdown-field ${formErrors.students ? 'error' : ''}`}
                onClick={toggleStudentDropdown}
              >
                <span className={selectedStudents.length > 0 ? 'selected-text' : 'placeholder-text'}>
                  {selectedStudents.length > 0 
                    ? `${selectedStudents.length} student${selectedStudents.length > 1 ? 's' : ''} selected` 
                    : 'Select Students'}
                </span>
                <span className="dropdown-icon">▼</span>
              </div>
              {formErrors.students && <div className="error-text">{formErrors.students}</div>}
            </div>
            
            {/* Students Dropdown */}
            {showStudentsDropdown && (
              <>
                <div className="dropdown-overlay" onClick={handleOutsideClick}></div>
                <div className="dropdown-container students-dropdown">
                  {students.length === 0 ? (
                    <div className="empty-dropdown">
                      No students found for this class
                    </div>
                  ) : (
                    <div className="dropdown-list">
                      {students.map(student => (
                        <div
                          key={`student-${student.id}`}
                          className={`dropdown-item student-item ${
                            selectedStudents.some(s => s.id === student.id) ? 'selected' : ''
                          }`}
                          onClick={() => handleToggleStudentSelection(student)}
                        >
                          <span>{student.name}</span>
                          {selectedStudents.some(s => s.id === student.id) && (
                            <span className="checkmark">✓</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            
            {/* Selected Students Pills */}
            {selectedStudents.length > 0 && (
              <div className="selected-items-container">
                {selectedStudents.map((student) => (
                  <div 
                    key={`selected-${student.id}`} 
                    className="selected-item-pill"
                  >
                    <span>{student.name}</span>
                    <button 
                      type="button" 
                      className="remove-pill-btn"
                      onClick={() => handleToggleStudentSelection(student)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Due Date Section */}
          <div className="form-section">
            <h2>Due Date</h2>
            <div className="form-group">
              <input
                type="date"
                id="dueDate"
                value={selectedDate}
                onChange={handleDateChange}
                className={formErrors.dueDate ? 'error' : ''}
              />
              {formErrors.dueDate && <div className="error-text">{formErrors.dueDate}</div>}
            </div>
          </div>
          
          {/* Enrichment Activities */}
          <div className="form-section">
            <h2>Enrichment Activities</h2>
            <div className="form-group switch-container">
              <label htmlFor="matchingQuiz">Include Ayats Meaning Quiz</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  id="matchingQuiz"
                  checked={matchingQuiz}
                  onChange={handleMatchingQuizToggle}
                />
                <label htmlFor="matchingQuiz" className="switch-label"></label>
              </div>
            </div>
          </div>

          {/* Phase 1.6: Recalculation Option (Only in edit mode for plan-generated assignments) */}
          {isEditMode && fetchedAssignmentData?.planId && (
            <div className="form-section">
              <h2>Assignment Recalculation</h2>
              <div className="form-group">
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    color: (fetchedAssignmentData.status === 'completed' || subsequentPendingCount === 0) ? '#999' : '#333',
                    cursor: (fetchedAssignmentData.status === 'completed' || subsequentPendingCount === 0) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={recalculateAfter}
                    onChange={(e) => setRecalculateAfter(e.target.checked)}
                    disabled={fetchedAssignmentData.status === 'completed' || subsequentPendingCount === 0}
                    style={{
                      cursor: (fetchedAssignmentData.status === 'completed' || subsequentPendingCount === 0) ? 'not-allowed' : 'pointer'
                    }}
                  />
                  <span>Recalculate all assignments after this one</span>
                </label>
                {recalculateAfter && subsequentPendingCount > 0 && (
                  <div style={{
                    marginTop: '8px',
                    marginLeft: '24px',
                    padding: '8px 12px',
                    backgroundColor: '#e8f4fd',
                    border: '1px solid #0066cc',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: '#0066cc'
                  }}>
                    ℹ️ This will delete and regenerate {subsequentPendingCount} pending assignment{subsequentPendingCount !== 1 ? 's' : ''}
                  </div>
                )}
                {fetchedAssignmentData.status === 'completed' && (
                  <div style={{
                    marginTop: '8px',
                    marginLeft: '24px',
                    fontSize: '12px',
                    color: '#999'
                  }}>
                    Cannot recalculate completed assignments
                  </div>
                )}
                {subsequentPendingCount === 0 && fetchedAssignmentData.status !== 'completed' && (
                  <div style={{
                    marginTop: '8px',
                    marginLeft: '24px',
                    fontSize: '12px',
                    color: '#999'
                  }}>
                    This is the last pending assignment. No recalculation needed.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <Link to="/assignments" className="cancel-btn">
              Cancel
            </Link>
            
            {/* Delete Button (Only show in edit mode) */}
            {isEditMode && (
              <button
                type="button"
                className="delete-btn"
                onClick={handleDeleteAssignment}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Delete Assignment'}
              </button>
            )}
            
            <button 
              type="submit" 
              className="submit-btn"
              disabled={loading}
            >
              {loading ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Assignment' : 'Create Assignment')}
            </button>
          </div>
        </form>
      </div>
      
      {/* Verse Preview Modal */}
      {showVersePreview && (
        <div className="verse-preview-modal">
          <VersePreview
            verses={verses}
            teachingTips={teachingTips}
            loading={loadingVerses}
            onClose={() => setShowVersePreview(false)}
          />
        </div>
      )}

      {/* Phase 1.6: Recalculation Confirmation Dialog */}
      {showRecalculationConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001
          }}
          onClick={() => setShowRecalculationConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '8px',
              padding: '24px',
              maxWidth: '450px',
              width: '90%'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, marginBottom: '16px', color: '#d9534f' }}>
              ⚠️ Confirm Recalculation
            </h3>

            <div style={{ marginBottom: '20px', fontSize: '14px', color: '#333', lineHeight: '1.6' }}>
              <p style={{ marginBottom: '12px' }}>This will:</p>
              <ul style={{ marginLeft: '20px', marginBottom: '12px' }}>
                <li>Delete {subsequentPendingCount} pending assignment{subsequentPendingCount !== 1 ? 's' : ''}</li>
                <li>Regenerate new assignments starting from where this assignment ends</li>
                <li>Preserve all completed assignments</li>
              </ul>
              <p style={{ marginBottom: 0, fontWeight: '500' }}>
                Completed work will NOT be affected.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowRecalculationConfirm(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: '1px solid #ddd',
                  backgroundColor: 'white',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowRecalculationConfirm(false);
                  await performSubmit();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '4px',
                  border: 'none',
                  backgroundColor: '#d9534f',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Confirm Recalculation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateAssignmentPage;